import { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PartyPopper } from "lucide-react";
import { eur } from "@/lib/format";

const COLORS = ["#0B6BCB", "#0A5CB0", "#7dd3fc", "#ffffff", "#38bdf8", "#0ea5e9"];

export function PrizeUnlockCelebration({ open, prizeName, amount, onClose }) {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(onClose, 4200);
    return () => clearTimeout(t);
  }, [open, onClose]);

  const pieces = useMemo(
    () =>
      Array.from({ length: 70 }).map((_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.5,
        color: COLORS[i % COLORS.length],
        rot: Math.random() * 720 - 360,
        dur: 2.6 + Math.random() * 1.6,
        size: 6 + Math.random() * 6,
      })),
    // regenerate each time it opens
    [open] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="prize-celebration"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          {pieces.map((p) => (
            <motion.div
              key={p.id}
              className="absolute top-0 rounded-sm"
              style={{ left: `${p.x}%`, width: p.size, height: p.size * 1.4, background: p.color }}
              initial={{ y: -40, opacity: 1, rotate: 0 }}
              animate={{ y: "105vh", rotate: p.rot, opacity: [1, 1, 0.9, 0] }}
              transition={{ duration: p.dur, delay: p.delay, ease: "easeIn" }}
            />
          ))}
          <motion.div
            className="relative z-10 text-center px-10 py-10 rounded-2xl border border-[#0B6BCB]/40 bg-[#FFFFFF]"
            style={{ boxShadow: "0 0 60px rgba(41,182,246,0.25)" }}
            initial={{ scale: 0.7, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 18 }}
          >
            <motion.div
              className="inline-flex"
              animate={{ rotate: [0, -14, 14, -8, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
            >
              <PartyPopper className="w-16 h-16 text-[#0B6BCB]" strokeWidth={1.5} />
            </motion.div>
            <h2 className="font-display font-extrabold text-3xl mt-4 tracking-tight text-[#0F1E33]" data-testid="celebration-title">
              Premio sbloccato!
            </h2>
            {prizeName && <p className="text-[#5A6B85] mt-1">{prizeName}</p>}
            <div className="font-display font-bold text-4xl text-[#0B6BCB] mt-3">{eur(amount)}</div>
            <p className="text-xs text-[#94A3B8] mt-4">Tocca per continuare</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
